import type { MinMaxOverrideRecord } from './weldTelemetryClient'
import type { AggregateResult, ParameterKey } from './weldParameterAggregator'
import {
  NULL_STATS,
  STATS_COLUMN_KEYS,
  type ParamStats,
  type StatsColumnKey
} from './passAnalyticsStats'

/** Client aggregate `ParameterKey` → Pass Analytics stats column key. */
const CLIENT_PARAM_TO_STATS: Partial<Record<ParameterKey, StatsColumnKey>> = {
  travelSpeed: 'travelSpeed',
  volts: 'volts',
  amps: 'amps',
  wireFeed: 'wireIpm',
  oscWidth: 'widthMil',
  target: 'targetVlt',
  horizontalBias: 'csBias',
  frequency: 'bpm',
  totalWireConsumed: 'totWireIn',
  trueEnergy: 'trueEnergy',
  heatInput: 'heat'
}

function buildPersistedStatsFromRecords(
  records: MinMaxOverrideRecord[]
): Record<StatsColumnKey, ParamStats> {
  const out = {} as Record<StatsColumnKey, ParamStats>
  for (const key of STATS_COLUMN_KEYS) out[key] = NULL_STATS
  for (const rec of records) {
    if ((STATS_COLUMN_KEYS as readonly string[]).includes(rec.parameter)) {
      out[rec.parameter as StatsColumnKey] = {
        min: rec.min,
        avg: rec.avg,
        max: rec.max
      }
    }
  }
  return out
}

function hasAnyFiniteStat(stats: ParamStats): boolean {
  return (
    (stats.min !== null && Number.isFinite(stats.min)) ||
    (stats.avg !== null && Number.isFinite(stats.avg)) ||
    (stats.max !== null && Number.isFinite(stats.max))
  )
}

function buildAggregateStatsForPass(
  aggregate: AggregateResult | null | undefined,
  passLabel: string
): Record<StatsColumnKey, ParamStats> {
  const out = {} as Record<StatsColumnKey, ParamStats>
  for (const key of STATS_COLUMN_KEYS) out[key] = NULL_STATS
  if (!aggregate?.rows?.length || !passLabel) return out

  const row = aggregate.rows.find((r) => r.pass === passLabel)
  if (!row) return out

  for (const [clientKey, statsKey] of Object.entries(CLIENT_PARAM_TO_STATS)) {
    const triple = row.values[clientKey as ParameterKey]
    if (triple && hasAnyFiniteStat(triple)) {
      out[statsKey as StatsColumnKey] = {
        min: triple.min,
        avg: triple.avg,
        max: triple.max
      }
    }
  }
  return out
}

/**
 * Baseline stats for the Recalculate dialog "Previous" column.
 * Priority: saved min/max overrides → aggregate values (Welding Parameters display).
 */
export function buildPreviousStatsForPass(input: {
  persistedOverrides: MinMaxOverrideRecord[]
  aggregate: AggregateResult | null | undefined
  passLabel: string
}): Record<StatsColumnKey, ParamStats> {
  const fromPersisted = buildPersistedStatsFromRecords(input.persistedOverrides)
  const fromAggregate = buildAggregateStatsForPass(input.aggregate, input.passLabel)

  const out = {} as Record<StatsColumnKey, ParamStats>
  for (const key of STATS_COLUMN_KEYS) {
    const persisted = fromPersisted[key]
    if (hasAnyFiniteStat(persisted)) {
      out[key] = persisted
      continue
    }
    out[key] = fromAggregate[key]
  }
  return out
}
